import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InfoCircleIcon } from 'assets/icons';
import Popover from 'components/Popover';
import PrivateComponent from 'composers/PrivateComponent';
import { CreatePromotionStepProps, PromotionTypeLocal } from 'domains/promotion/Promotion.types';
import { UserRole } from 'domains/user';
import { Flex, Icon, InputNumber, MarginBox, Radio, Text } from 'UI';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import { CommercialFamilyDiscountRow } from './CommercialFamilyDiscountRow';
import { FlashPromotionDiscount } from './FlashPromotionDiscount';
import { ReferencesDiscountRow } from './ReferencesDiscountRow';

function getPriceOptionContent(type: PromotionTypeLocal) {
  switch (type) {
    case 'PROMOTION_FLASH_QUANTITY_LIMITED':
      return {
        header: <Trans i18nKey={'backoffice.promotion.remote_product_selection'}>Select promoted product</Trans>,
        description: null,
        maxFileCount: 0,
        maxCommercialFamiliesGroups: 0,
        maxReferencesGroups: 0,
      };
    case 'BANNER':
      return {
        header: (
          <Trans i18nKey={'backoffice.promotion.banner.remote_products_selection'}>
            Select your promoted products (optional)
          </Trans>
        ),
        description: (
          <Trans i18nKey={'backoffice.promotion.remote_products_selection.description'}>
            Directly enter one or several commercial family code or/and upload a Csv file to select the promoted
            references
          </Trans>
        ),
        maxFileCount: 1,
        maxCommercialFamiliesGroups: 1,
        maxReferencesGroups: 1,
      };
    case 'PROMOTION_FLASH_TIME_LIMITED':
      return {
        header: (
          <Trans i18nKey={'backoffice.promotion.remote_products_family_codes_selection'}>
            Select promoted products
          </Trans>
        ),
        description: (
          <Trans i18nKey={'backoffice.promotion.remote_products_family_codes_selection_description'}>
            Add promoted items by searching family codes or promotion codes.
          </Trans>
        ),
        maxFileCount: 1,
        maxCommercialFamiliesGroups: 1,
        maxReferencesGroups: 1,
      };
    default:
      return {
        header: (
          <Trans i18nKey={'backoffice.promotion.remote_products_family_codes_selection'}>
            Select promoted products
          </Trans>
        ),
        description: (
          <Trans i18nKey={'backoffice.promotion.remote_products_family_codes_selection_description'}>
            Add promoted items by searching family codes or promotion codes.
          </Trans>
        ),
        maxFileCount: 5,
        maxCommercialFamiliesGroups: 20,
        maxReferencesGroups: 20,
      };
  }
}

export const ProductStep = ({ promotion, setPromotion }: CreatePromotionStepProps) => {
  const { t } = useTranslation();
  const content = getPriceOptionContent(promotion.promotionType);

  useResetScroll();

  const renderCalculationInfo = (method: 'PCL' | 'PNMRA') => {
    const infoData = {
      PCL: ['PCL-(DG%+MCX%)=PROMO PRICE', 'e.g. 300-(40%+10%)=150'],
      PNMRA: ['PCL-DG%=PNMRA', 'e.g. 300-40%=180', 'PNMRA-MCX%=PROMO PRICE', 'e.g. 180-10%=162'],
    };

    return (
      <Popover
        padding={10}
        content={
          <Flex direction={'column'}>
            <Text type="base" bold>
              {t('backoffice.promotion.calculation_method', 'Example of calculation method')}
            </Text>
            {infoData[method].map((i) => (
              <MarginBox mt={10} key={i}>
                <Text type={'text'}>{i}</Text>
              </MarginBox>
            ))}
          </Flex>
        }
        placement={'right'}
      >
        <MarginBox ml={3} mt={3}>
          <Icon IconComponent={InfoCircleIcon} size={16} />
        </MarginBox>
      </Popover>
    );
  };

  const renderPriceOption = () => {
    return (
      <Flex direction={'column'} data-cy={'price-options'}>
        <Text type={'h1_banner_light'}>{t('promotion.create_new.title.product', 'Product')}</Text>
        <Text type={'text'}>
          {t(
            'backoffice.promotion.select_calculation_option',
            'Please select desired calculation option of the promotion price.',
          )}
        </Text>
        <MarginBox mt={15} />
        <Flex direction={'row'}>
          <Radio
            disable={promotion.editing}
            label={t('backoffice.promotion.price_pcl', 'Based on PCL (Detail net price)')}
            checked={promotion.discountType === 'PCL'}
            onChange={() => setPromotion({ ...promotion, discountType: 'PCL' })}
            data-cy={'radio-based-on-pcl'}
          />
          {renderCalculationInfo('PCL')}
        </Flex>
        <MarginBox mt={15} />
        <Flex direction={'row'}>
          <Radio
            disable={promotion.editing}
            label={t('backoffice.promotion.pnmra', 'Based on PNMRA (Standard price for service)')}
            checked={promotion.discountType === 'PNMRA'}
            onChange={() => setPromotion({ ...promotion, discountType: 'PNMRA' })}
            data-cy={'radio-based-on-pnmra'}
          />
          {renderCalculationInfo('PNMRA')}
        </Flex>
      </Flex>
    );
  };

  const renderQuantity = () => {
    return (
      <Flex direction={'column'}>
        <Text type="heading3">
          {t('backoffice.promotion.promotion_flash_quantity_limited_mandatory.title', 'Quantity limited to*')}
        </Text>
        <Flex maxWidth={200}>
          <InputNumber
            disabled={promotion.editing}
            data-cy={'quantity-limited-to-input'}
            onBlur={(quantity) => {
              setPromotion({
                ...promotion,
                quantityLimitFlashPromotion: quantity ?? 0,
              });
            }}
            value={promotion.quantityLimitFlashPromotion !== 0 ? promotion.quantityLimitFlashPromotion : undefined}
            placeholder={t(
              'backoffice.promotion.promotion_flash_quantity_limited_mandatory.placeholder',
              'Add your quantity here',
            )}
            min={0}
            max={1000000}
          />
        </Flex>
      </Flex>
    );
  };

  const renderPromotedProducts = () => {
    return (
      <Flex direction={'column'} data-cy={'select-promoted-products-section'}>
        <MarginBox mt={45} />
        <Text type="heading3">{content.header}</Text>
        <Text type={'text'}>{content.description}</Text>
        {promotion.promotionType === 'PROMOTION_FLASH_QUANTITY_LIMITED' && renderQuantity()}
        {content.maxCommercialFamiliesGroups > 0 && (
          <Flex direction={'column'}>
            <CommercialFamilyDiscountRow
              promotion={promotion}
              setPromotion={setPromotion}
              setCurrentStep={() => undefined}
              maxCommercialFamiliesGroups={content.maxCommercialFamiliesGroups}
            />
            <MarginBox mt={30} />
          </Flex>
        )}
        <Flex direction={'column'}>
          <ReferencesDiscountRow
            promotion={promotion}
            setPromotion={setPromotion}
            setCurrentStep={() => undefined}
            maxFileCount={content.maxFileCount}
            maxReferencesGroups={content.maxReferencesGroups}
          />
          <MarginBox mt={15} />
        </Flex>
      </Flex>
    );
  };

  const renderAdditionalDiscount = () => {
    return (
      <PrivateComponent
        render={() => (
          <>
            {promotion.promotionType === 'DISCOUNT' && (
              <Flex direction={'column'} minWidth={200} maxWidth={200}>
                <Text type={'h5_bold'}>
                  {t('backoffice.promotion.add_additional_discount', 'Add additional discount')}
                </Text>
                <MarginBox mt={20} />
                <Flex minWidth={115} maxWidth={115}>
                  <InputNumber
                    disabled={promotion.editing}
                    placeholder={'-'}
                    addonAfter={'%'}
                    min={0}
                    max={100}
                    value={promotion.additionalInformation?.discount}
                    onChangeDebounced={(discount) => {
                      setPromotion({
                        ...promotion,
                        additionalInformation: {
                          ...promotion.additionalInformation,
                          discount: Number(discount),
                        },
                      });
                    }}
                  />
                </Flex>
                <MarginBox mt={30} />
              </Flex>
            )}
          </>
        )}
        requiredRights={[UserRole.COUNTRY_ADMIN]}
      />
    );
  };

  const getCurrentItem = () => {
    switch (promotion.promotionType) {
      case 'BANNER':
        return <>{renderPromotedProducts()}</>;
      case 'PROMOTION_FLASH_QUANTITY_LIMITED':
      case 'PROMOTION_FLASH_TIME_LIMITED':
        return (
          <>
            {renderPriceOption()}
            {renderPromotedProducts()}
            <FlashPromotionDiscount promotion={promotion} setPromotion={setPromotion} />
          </>
        );
      default:
        return (
          <>
            {renderPriceOption()}
            {renderPromotedProducts()}
            {renderAdditionalDiscount()}
          </>
        );
    }
  };

  return (
    <Flex direction={'column'} size={0} data-cy={'product-step-page'}>
      {getCurrentItem()}
    </Flex>
  );
};
